import { createContext, useContext } from 'react';

import { Orientation } from './base/Orientation';
import { Point } from './base/Point';

import type { Size } from './base/Point';
export type LayoutDimension = {
  size: Size;
  orientation: Orientation;
  origin: Size;
  spacing: number;
};

export type LayoutContextProps = {
  layout: LayoutDimension;
  points: string;
};

export const LAYOUT_FLAT = new Orientation(
  3.0 / 2.0,
  0.0,
  Math.sqrt(3.0) / 2.0,
  Math.sqrt(3.0),
  2.0 / 3.0,
  0.0,
  -1.0 / 3.0,
  Math.sqrt(3.0) / 3.0,
  0.0
);

export const LAYOUT_POINTY = new Orientation(
  Math.sqrt(3.0),
  Math.sqrt(3.0) / 2.0,
  0.0,
  3.0 / 2.0,
  Math.sqrt(3.0) / 3.0,
  -1.0 / 3.0,
  0.0,
  2.0 / 3.0,
  0.5
);

export const defaultSize = new Point(10, 10);
export const defaultOrigin = new Point(0, 0);
export const defaultSpacing = 1.0;

export const LayoutContext = createContext<LayoutContextProps>({
  layout: {
    size: defaultSize,
    orientation: LAYOUT_FLAT,
    origin: defaultOrigin,
    spacing: defaultSpacing,
  },
  points: '',
});

export function useLayoutContext() {
  return useContext(LayoutContext);
}

/**
 * Calculates the points for a hexagon given the size, angle, and center
 * @param circumradius Radius of the Hexagon
 * @param angle Angle offset for the hexagon in radians
 * @param center Central point for the heaxagon
 * @returns Array of 6 points
 */
export function calculateCoordinates(
  circumradius: number,
  angle: number = 0,
  center: Point = new Point(0, 0)
) {
  const corners: Point[] = [];
  for (let i = 0; i < 6; i++) {
    const x = circumradius * Math.cos((2 * Math.PI * i) / 6 + angle);
    const y = circumradius * Math.sin((2 * Math.PI * i) / 6 + angle);
    const point = new Point(center.x + x, center.y + y);
    corners.push(point);
  }
  return corners;
}

export default LayoutContext;
